import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ApiFout, api } from '@/lib/api';
import type { FactuurPeriode } from '@/lib/api-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Loader2, Save, Receipt, Plus, Trash2, CalendarIcon, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * Een factuurperiode. Gelijk aan FactuurPeriode uit api-types; de alias blijft omdat
 * de rest van dit bestand hem zo noemt.
 */
type BillingSettings = FactuurPeriode;

/**
 * De melding bij een mislukte opslag, met de veldnamen erbij als de route ze
 * meestuurt.
 *
 * Zonder die namen is "Ongeldige waarden." niet te herleiden: de route stuurt bij
 * een zod-fout wél in `velden` welk veld het is, maar dat werd hier weggegooid en
 * dan blijft er een melding over waar een beheerder niets mee kan.
 */
function foutmelding(fout: unknown, standaard: string): string {
  if (!(fout instanceof ApiFout)) return standaard;
  if (!fout.velden?.length) return fout.message;
  return `${fout.message} (${fout.velden.map((v) => v.veld).join(', ')})`;
}

export function BillingSettingsTab() {
  const [periods, setPeriods] = useState<BillingSettings[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const fetchPeriods = async () => {
    try {
      const { periods: gevonden } = await api.get<{ periods: FactuurPeriode[] }>('/admin/billing');
      setPeriods(gevonden);
    } catch (fout) {
      toast.error(foutmelding(fout, 'Kon de factuurperiodes niet laden'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchPeriods();
  }, []);

  const hasOverlap = (period: BillingSettings): boolean => {
    const start = new Date(period.startDate);
    const end = period.endDate ? new Date(period.endDate) : null;

    return periods.some(other => {
      if (other.id === period.id) return false;
      const otherStart = new Date(other.startDate);
      const otherEnd = other.endDate ? new Date(other.endDate) : null;
      const startsBeforeOtherEnds = otherEnd === null || start <= otherEnd;
      const endsAfterOtherStarts = end === null || end >= otherStart;
      return startsBeforeOtherEnds && endsAfterOtherStarts;
    });
  };

  const handleSave = async (period: BillingSettings) => {
    if (!period.startDate) {
      toast.error('Startdatum is verplicht');
      return;
    }
    if (period.endDate && new Date(period.endDate) <= new Date(period.startDate)) {
      toast.error('Einddatum moet na de startdatum liggen');
      return;
    }
    if (hasOverlap(period)) {
      toast.error('Deze periode overlapt met een bestaande periode');
      return;
    }
    setSavingId(period.id);
    try {
      await api.patch(`/admin/billing/${period.id}`, {
        voicePurchasePricePerSecond: period.voicePurchasePricePerSecond,
        voiceSellingPricePerSecond: period.voiceSellingPricePerSecond,
        llmMarginPercentage: period.llmMarginPercentage,
        startDate: period.startDate,
        endDate: period.endDate,
      });
      toast.success('Factuurperiode opgeslagen');
      await fetchPeriods();
    } catch (fout) {
      toast.error(foutmelding(fout, 'Opslaan mislukt'));
    } finally {
      setSavingId(null);
    }
  };

  const handleAddPeriod = async () => {
    if (isAdding) return;
    
    // Check if there's already an unsaved period with zero values (just added)
    const hasEmpty = periods.some(p => 
      p.voicePurchasePricePerSecond === 0 && 
      p.voiceSellingPricePerSecond === 0 && 
      p.llmMarginPercentage === 0
    );
    if (hasEmpty) {
      toast.error('Vul eerst de bestaande lege periode in voordat u een nieuwe toevoegt');
      return;
    }

    setIsAdding(true);
    try {
      await api.post('/admin/billing', {
        voicePurchasePricePerSecond: 0,
        voiceSellingPricePerSecond: 0,
        llmMarginPercentage: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: null,
      });
      toast.success('Nieuwe periode toegevoegd');
      await fetchPeriods();
    } catch (fout) {
      toast.error(foutmelding(fout, 'Nieuwe periode aanmaken mislukt'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // De route weigert een periode waarin verbruik valt. Dat is geen formaliteit:
      // de tarieven staan niet bij de verbruiksregel maar worden bij het uitlezen
      // toegepast, dus een periode weghalen verandert de kosten van elke regel die
      // erin viel -- ook die van een al afgesloten maand.
      await api.delete(`/admin/billing/${id}`);
      toast.success('Periode verwijderd');
      setPeriods((prev) => prev.filter((p) => p.id !== id));
    } catch (fout) {
      toast.error(foutmelding(fout, 'Verwijderen mislukt'));
    }
  };

  const updatePeriod = (id: string, updates: Partial<BillingSettings>) => {
    setPeriods(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-5 w-5" />
            Facturatie Stamgegevens
          </CardTitle>
          <Button variant="outline" size="sm" onClick={handleAddPeriod} disabled={isAdding}>
            {isAdding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Nieuwe periode
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {periods.length === 0 && (
          <p className="text-sm text-muted-foreground italic">Geen facturatie-periodes gevonden.</p>
        )}

        {periods.map((period) => (
          <BillingPeriodCard
            key={period.id}
            period={period}
            hasOverlap={hasOverlap(period)}
            isSaving={savingId === period.id}
            canDelete={periods.length > 1}
            onUpdate={(updates) => updatePeriod(period.id, updates)}
            onSave={() => handleSave(period)}
            onDelete={() => handleDelete(period.id)}
          />
        ))}
      </CardContent>
    </Card>
  );
}

/* ─── Period Card ─── */

function BillingPeriodCard({
  period,
  hasOverlap,
  isSaving,
  canDelete,
  onUpdate,
  onSave,
  onDelete,
}: {
  period: BillingSettings;
  hasOverlap: boolean;
  isSaving: boolean;
  canDelete: boolean;
  onUpdate: (updates: Partial<BillingSettings>) => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  const margin = period.voiceSellingPricePerSecond - period.voicePurchasePricePerSecond;
  const marginPct = period.voicePurchasePricePerSecond > 0
    ? ((margin / period.voicePurchasePricePerSecond) * 100).toFixed(1)
    : '—';
  const dateInvalid = period.endDate && new Date(period.endDate) <= new Date(period.startDate);

  return (
    <div
      className={cn(
        "border rounded-lg p-5 space-y-5 transition-colors",
        hasOverlap && "border-destructive bg-destructive/5",
        dateInvalid && !hasOverlap && "border-destructive/50 bg-muted"
      )}
    >
      {/* Warnings */}
      {hasOverlap && (
        <WarningBanner>Deze periode overlapt met een andere periode</WarningBanner>
      )}
      {dateInvalid && !hasOverlap && (
        <WarningBanner>Einddatum moet na de startdatum liggen</WarningBanner>
      )}

      {/* Dates */}
      <div className="flex flex-wrap items-end gap-6">
        <DateField
          label="Startdatum"
          value={period.startDate ? new Date(period.startDate) : undefined}
          onChange={(d) => onUpdate({ startDate: d ? format(d, 'yyyy-MM-dd') : '' })}
        />
        <DateField
          label="Einddatum"
          value={period.endDate ? new Date(period.endDate) : undefined}
          onChange={(d) => onUpdate({ endDate: d ? format(d, 'yyyy-MM-dd') : null })}
          placeholder="Onbepaald"
        />
      </div>

      <Separator />

      {/* Voice pricing */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Spraakopname
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PriceInput
            label="Inkoopprijs / seconde"
            value={period.voicePurchasePricePerSecond}
            onChange={(v) => onUpdate({ voicePurchasePricePerSecond: v })}
          />
          <PriceInput
            label="Verkoopprijs / seconde"
            value={period.voiceSellingPricePerSecond}
            onChange={(v) => onUpdate({ voiceSellingPricePerSecond: v })}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Marge: <span className="font-medium text-foreground">€{margin.toFixed(4)}</span>/s ({marginPct}%)
        </p>
      </fieldset>

      <Separator />

      {/* LLM */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          LLM Calls
        </legend>
        <div className="max-w-[200px] space-y-1.5">
          <Label className="text-sm">Verkoopmarge (%)</Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            value={period.llmMarginPercentage}
            onChange={(e) => onUpdate({ llmMarginPercentage: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </fieldset>

      <Separator />

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <Button onClick={onSave} disabled={isSaving} size="sm">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Opslaan
        </Button>
        {canDelete && (
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={onDelete}>
            <Trash2 className="h-4 w-4 mr-1" />
            Verwijderen
          </Button>
        )}
      </div>
    </div>
  );
}

/* ─── Reusable sub-components ─── */

function WarningBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-destructive font-medium bg-destructive/10 rounded-md px-3 py-2">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      {children}
    </div>
  );
}

function PriceInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
        <Input
          type="number"
          step="0.0001"
          min="0"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="pl-7"
        />
      </div>
    </div>
  );
}

function DateField({ label, value, onChange, placeholder = 'Kies datum' }: {
  label: string;
  value?: Date;
  onChange: (d: Date | undefined) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[180px] justify-start text-left font-normal",
              !value && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            {value ? format(value, 'dd-MM-yyyy') : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            initialFocus
            className="p-3 pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
